import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { storableError } from '../../util/errors';
import { parse, getValidInboxSort } from '../../util/urlHelpers';
import { getSupportedProcessesInfo } from '../../transactions/transaction';
import { states as bookingStates } from '../../transactions/transactionProcessBooking';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';

const PAYMENTS_PAGE_SIZE = 10;

const asTimestampRangeStart = value => {
  if (!value) {
    return `${new Date().toISOString()},`;
  }
  return value.includes(',') ? value : `${value},`;
};
const PAYMENT_SCOPE_ALL = 'all';
const PAYMENT_SCOPE_PENDING = 'pending';
const PAYMENT_SCOPE_COMPLETED = 'completed';
const PAYMENT_SCOPE_REFUNDED = 'refunded';
const PAYMENT_SCOPE_OPTIONS = [
  PAYMENT_SCOPE_ALL,
  PAYMENT_SCOPE_PENDING,
  PAYMENT_SCOPE_COMPLETED,
  PAYMENT_SCOPE_REFUNDED,
];
const PAYMENT_SCOPE_STATE_MAP = {
  [PAYMENT_SCOPE_PENDING]: [bookingStates.PREAUTHORIZED, bookingStates.ACCEPTED],
  [PAYMENT_SCOPE_COMPLETED]: [
    bookingStates.DELIVERED,
    bookingStates.REVIEWED,
    bookingStates.REVIEWED_BY_CUSTOMER,
    bookingStates.REVIEWED_BY_PROVIDER,
  ],
  [PAYMENT_SCOPE_REFUNDED]: [
    bookingStates.DECLINED,
    bookingStates.CANCELED,
    bookingStates.EXPIRED,
    bookingStates.PAYMENT_EXPIRED,
  ],
};
const PROVIDER_PAYOUT_RELEVANT_STATES = [
  bookingStates.PREAUTHORIZED,
  bookingStates.ACCEPTED,
  bookingStates.DELIVERED,
  bookingStates.REVIEWED,
  bookingStates.REVIEWED_BY_CUSTOMER,
  bookingStates.REVIEWED_BY_PROVIDER,
];
const selectedPaymentScopes = value => {
  const parsed = typeof value === 'string' ? value.split(',') : [];
  const valid = parsed.filter(scope => PAYMENT_SCOPE_OPTIONS.includes(scope));
  return valid.length > 0 ? valid : [PAYMENT_SCOPE_ALL];
};
const statesFromScopes = scopes => {
  const selected = scopes.filter(scope => scope !== PAYMENT_SCOPE_ALL);
  const states = selected.reduce((acc, scope) => {
    const next = PAYMENT_SCOPE_STATE_MAP[scope] || [];
    return [...acc, ...next];
  }, []);
  return [...new Set(states)];
};

// ================ Helper functions ================ //

const entityRefs = entities =>
  entities.map(entity => ({
    id: entity.id,
    type: entity.type,
  }));

// ================ Slice ================ //

const paymentsPageSlice = createSlice({
  name: 'PaymentsPage',
  initialState: {
    fetchInProgress: false,
    fetchOrdersOrSalesError: null,
    pagination: null,
    transactionRefs: [],
  },
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(loadDataThunk.pending, state => {
        state.fetchInProgress = true;
        state.fetchOrdersOrSalesError = null;
      })
      .addCase(loadDataThunk.fulfilled, (state, action) => {
        const { transactions, pagination } = action.payload;
        state.fetchInProgress = false;
        state.transactionRefs = entityRefs(transactions);
        state.pagination = pagination;
      })
      .addCase(loadDataThunk.rejected, (state, action) => {
        console.error(action.payload || action.error); // eslint-disable-line
        state.fetchInProgress = false;
        state.fetchOrdersOrSalesError = action.payload;
      });
  },
});

export default paymentsPageSlice.reducer;

// ================ Load data ================ //

const loadDataPayloadCreator = ({ params, search }, { dispatch, rejectWithValue, extra: sdk }) => {
  const { tab } = params;

  const onlyFilterValues = {
    orders: 'order',
    sales: 'sale',
  };

  const onlyFilter = onlyFilterValues[tab];
  if (!onlyFilter) {
    return Promise.reject(new Error(`Invalid tab for PaymentsPage: ${tab}`));
  }

  const { page = 1, sort, bookingStart, bookingScope, paymentStates } = parse(search);
  const sortFromUrl = getValidInboxSort(sort);
  const sortParams = Object.keys(sortFromUrl).length ? sortFromUrl : getValidInboxSort('newest');
  const bookingStartMaybe =
    sortParams?.sort === '-bookingStart' && bookingScope !== 'all'
      ? { bookingStart: asTimestampRangeStart(bookingStart) }
      : {};
  const scopes = selectedPaymentScopes(paymentStates);
  const states =
    tab === 'orders' ? statesFromScopes(scopes) : tab === 'sales' ? PROVIDER_PAYOUT_RELEVANT_STATES : [];
  const statesMaybe = states.length > 0 ? { states: states.map(s => `state/${s}`).join(',') } : {};
  const processNames = getSupportedProcessesInfo().map(p => p.name);

  const apiQueryParams = {
    only: onlyFilter,
    processNames,
    hasPayin: true,
    include: [
      'listing',
      'provider',
      'provider.profileImage',
      'customer',
      'customer.profileImage',
      'booking',
    ],
    'fields.listing': ['title', 'availabilityPlan', 'publicData.listingType'],
    'fields.user': ['profile.displayName', 'profile.abbreviatedName', 'deleted', 'banned'],
    'fields.image': ['variants.square-small', 'variants.square-small2x'],
    page,
    perPage: PAYMENTS_PAGE_SIZE,
    ...bookingStartMaybe,
    ...statesMaybe,
    ...sortParams,
  };

  return sdk.transactions
    .query(apiQueryParams)
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      const transactions = response?.data?.data || [];
      const pagination = response?.data?.meta || null;
      return { transactions, pagination };
    })
    .catch(e => {
      return rejectWithValue(storableError(e));
    });
};

export const loadDataThunk = createAsyncThunk('PaymentsPage/loadData', loadDataPayloadCreator);

// Backward compatible wrapper for the thunk
export const loadData = (params, search) => (dispatch, getState, sdk) => {
  return dispatch(loadDataThunk({ params, search }));
};
